import React, { useState, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { Search, X, UserPlus, Phone, ChevronRight } from 'lucide-react';
import { searchCustomers } from '../../services/dashboardApi';
import AddPartyModal from '../parties/AddPartyModal';

const PartySelectModal = ({ isOpen, onSelect, onClose, partyType = 'all' }) => {
  const { t } = useLang();
  const [query, setQuery] = useState('');
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddParty, setShowAddParty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadParties();
    }
  }, [isOpen, query]);

  const loadParties = async () => {
    try {
      setLoading(true);
      const response = await searchCustomers(query);
      if (response && response.success) {
        let data = response.customers || [];
        // Filter by party type if specified
        if (partyType === 'customer') {
          data = data.filter(p => (p.is_supplier === 0 || p.is_supplier === '0' || p.type?.toLowerCase() === 'customer'));
        } else if (partyType === 'supplier') {
          data = data.filter(p => (p.is_supplier === 1 || p.is_supplier === '1' || p.type?.toLowerCase() === 'supplier'));
        }
        
        // Local text search filter
        if (query.trim()) {
          const q = query.toLowerCase().trim();
          data = data.filter(p => 
            (p.name && p.name.toLowerCase().includes(q)) || 
            (p.mobile && p.mobile.includes(q))
          );
        }
        
        setParties(data);
      }
    } catch (error) {
      console.error('Failed to load parties:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.titleWrap}>
            <h3 style={styles.title}>{t(LangKeys.add_bill_search_parties)}</h3>
            <span style={styles.subTitle}>{parties.length} {t(LangKeys.parties || 'Parties')}</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose}><X size={24} /></button>
        </div>

        <div style={styles.searchBar}>
          <Search size={20} color="#94A3B8" />
          <input 
            autoFocus
            placeholder={t(LangKeys.add_bill_search_parties)} 
            style={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div style={styles.list}>
          {loading ? (
            <div style={styles.loading}>{t(LangKeys.loading || 'Loading...')}</div>
          ) : parties.length === 0 ? (
            <div style={styles.empty}>
              <UserPlus size={48} color="#E2E8F0" />
              <p>{t(LangKeys.no_parties_found || 'No parties found')}</p>
            </div>
          ) : (
            parties.map(party => (
              <div key={party.id} style={styles.partyItem} onClick={() => onSelect({
                id: party.id,
                customerName: party.name,
                mobileNo: party.mobile,
                isSupplier: party.is_supplier === 1 || party.is_supplier === '1'
              })}>
                <div style={{
                  ...styles.partyAvatar,
                  backgroundColor: partyType === 'supplier' ? '#F0FDF4' : '#EFF6FF',
                  color: partyType === 'supplier' ? '#16A34A' : '#1D4ED8'
                }}>
                  {(party.name || 'P').charAt(0)}
                </div>
                <div style={styles.partyInfo}>
                  <span style={styles.partyName}>{party.name}</span>
                  <div style={styles.partyMeta}>
                    <Phone size={12} />
                    <span>{party.mobile}</span>
                  </div>
                </div>
                <div style={styles.partyRight}>
                   <span style={{
                     fontSize: '14px', 
                     fontWeight: '800', 
                     color: (parseFloat(party.balance) || 0) >= 0 ? '#10B981' : '#EF4444'
                   }}>
                     ₹ {Math.abs(parseFloat(party.balance) || 0).toLocaleString()}
                   </span>
                   <ChevronRight size={16} color="#CBD5E1" />
                </div>
              </div>
            ))
          )}
        </div>

        <button 
          style={{
            ...styles.addNewBtn,
            backgroundColor: partyType === 'supplier' ? '#16A34A' : '#1D4ED8',
            boxShadow: partyType === 'supplier' 
              ? '0 10px 20px rgba(22, 163, 74, 0.15)' 
              : '0 10px 20px rgba(29, 78, 216, 0.15)'
          }} 
          onClick={() => setShowAddParty(true)}
        >
          <UserPlus size={20} />
          <span>{t(LangKeys.add_bill_add_new_party)}</span>
        </button>
      </div>

      <AddPartyModal 
        isOpen={showAddParty} 
        onClose={() => setShowAddParty(false)}
        initialType={partyType === 'supplier' ? 'supplier' : 'customer'}
        onSuccess={(newParty) => {
          setShowAddParty(false);
          if (newParty && newParty.id) {
            onSelect({
              id: newParty.id,
              customerName: newParty.name,
              mobileNo: newParty.mobile,
              isSupplier: newParty.is_supplier === 1 || newParty.is_supplier === '1'
            });
          } else {
            loadParties();
          }
        }}
      />
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: 'white', width: '100%', maxWidth: '500px', borderRadius: '24px', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F1F5F9' },
  titleWrap: { display: 'flex', flexDirection: 'column' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0, color: '#1E293B' },
  subTitle: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748B' },
  searchBar: { margin: '16px 24px', padding: '0 16px', height: '48px', background: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '12px' },
  searchInput: { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '15px', fontWeight: '600' },
  list: { flex: 1, overflowY: 'auto', padding: '0 8px' },
  partyItem: { padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', borderRadius: '12px', transition: '0.2s' },
  partyAvatar: { width: '44px', height: '44px', borderRadius: '14px', background: '#EFF6FF', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '800' },
  partyInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  partyName: { fontSize: '15px', fontWeight: '700', color: '#1E293B' },
  partyMeta: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748B' },
  partyRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  empty: { padding: '60px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#94A3B8' },
  loading: { padding: '60px 24px', textAlign: 'center', color: '#94A3B8', fontWeight: '600' },
  addNewBtn: { margin: '16px 24px 24px', height: '52px', background: '#1D4ED8', color: 'white', borderRadius: '14px', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 10px 20px rgba(29, 78, 216, 0.15)' }
};

export default PartySelectModal;
